import { 
  IptvSource, 
  IptvChannel, 
  IptvVodMovie, 
  IptvSeries, 
  IptvCategory, 
  IptvSyncProgress 
} from '../types';
import { getApiBaseUrl, buildApiUrl, isNativeAndroidApp } from '../utils/apiConfig';

export const DEFAULT_USER_AGENT = 'IPTVSmartersPro';

// IndexedDB Cache for IPTV data (handles large 50k+ channel playlists without lagging React state)
const DB_NAME = 'GarCollections_IPTV_Cache';
const DB_VERSION = 1;

/**
 * Fetch helper with timeout to prevent network operations from hanging indefinitely
 */
export async function fetchWithTimeout(url: string, options: RequestInit = {}, timeoutMs = 20000): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(id);
    return res;
  } catch (err: any) {
    clearTimeout(id);
    if (err.name === 'AbortError') {
      throw new Error(`Temps d'espera esgotat (${Math.round(timeoutMs / 1000)}s) en connectar amb el servidor IPTV.`);
    }
    throw err;
  }
}

function openIptvDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      return reject(new Error('IndexedDB no suportat en aquest entorn'));
    }

    const timeout = setTimeout(() => {
      reject(new Error("Temps d'espera esgotat en obrir IndexedDB"));
    }, 3000);

    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('channels')) {
          db.createObjectStore('channels', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('movies')) {
          db.createObjectStore('movies', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('series')) {
          db.createObjectStore('series', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('categories')) {
          db.createObjectStore('categories', { keyPath: 'id' });
        }
      };
      request.onsuccess = () => {
        clearTimeout(timeout);
        resolve(request.result);
      };
      request.onerror = () => {
        clearTimeout(timeout);
        reject(request.error || new Error('Error en obrir la base de dades local IndexedDB'));
      };
      request.onblocked = () => {
        clearTimeout(timeout);
        reject(new Error('Base de dades local bloquejada per un altre procés'));
      };
    } catch (e) {
      clearTimeout(timeout);
      reject(e);
    }
  });
}

/**
 * Proxy a stream URL via backend with default User-Agent 'IPTVSmartersPro'
 * In Android Native App / Android TV, returns direct streamUrl so ExoPlayer and external players stream natively.
 */
export function getProxiedStreamUrl(streamUrl: string, userAgent = DEFAULT_USER_AGENT): string {
  if (!streamUrl) return '';
  // If running in Native Android app (APK / Android TV), return direct stream URL
  if (isNativeAndroidApp()) {
    return streamUrl;
  }
  if (streamUrl.startsWith('http') && streamUrl.includes('/api/iptv/proxy-stream')) return streamUrl;
  
  const path = `/api/iptv/proxy-stream?url=${encodeURIComponent(streamUrl)}&userAgent=${encodeURIComponent(userAgent || DEFAULT_USER_AGENT)}`;
  return buildApiUrl(path);
}

/**
 * Proxy an image URL (logo / poster) to prevent Mixed Content (http->https) and ORB errors.
 * In Android Native App, direct image loading is allowed without CORS issues.
 */
export function getProxiedImageUrl(imageUrl?: string, userAgent = DEFAULT_USER_AGENT): string {
  if (!imageUrl || typeof imageUrl !== 'string') return '';
  const trimmed = imageUrl.trim();
  if (!trimmed) return '';
  if (isNativeAndroidApp()) {
    return trimmed;
  }
  if (trimmed.startsWith('http') && trimmed.includes('/api/iptv/image-proxy')) return trimmed;
  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return trimmed;
  
  const path = `/api/iptv/image-proxy?url=${encodeURIComponent(trimmed)}&userAgent=${encodeURIComponent(userAgent || DEFAULT_USER_AGENT)}`;
  return buildApiUrl(path);
}

/**
 * Client-side M3U playlist parser fallback (for Android native execution or offline bypass)
 */
function parseClientM3uText(content: string, sourceId: string, defaultUa: string = DEFAULT_USER_AGENT) {
  const lines = content.split(/\r?\n/);
  const liveChannels: any[] = [];
  const vodMovies: any[] = [];
  const seriesList: any[] = [];
  const categoryMap = new Map<string, any>();

  let currentItem: any = null;
  let chIndex = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    if (line.startsWith('#EXTINF:')) {
      chIndex++;
      currentItem = {
        id: `${sourceId}_ch_${chIndex}`,
        sourceId,
        name: `Canal ${chIndex}`,
        streamUrl: '',
        category: 'General',
        userAgent: defaultUa
      };

      const infoCommaIdx = line.lastIndexOf(',');
      if (infoCommaIdx !== -1 && infoCommaIdx < line.length - 1) {
        currentItem.name = line.substring(infoCommaIdx + 1).trim() || `Canal ${chIndex}`;
      }

      const attrRegex = /([a-zA-Z0-9_-]+)=(?:"([^"]*)"|'([^']*)'|([^\s,]+))/g;
      let match;
      const attrHeader = infoCommaIdx !== -1 ? line.substring(0, infoCommaIdx) : line;
      while ((match = attrRegex.exec(attrHeader)) !== null) {
        const key = match[1].toLowerCase();
        const val = (match[2] !== undefined ? match[2] : match[3] !== undefined ? match[3] : match[4] || '').trim();
        if (key === 'tvg-id' || key === 'tvg-epg') {
          currentItem.tvgId = val;
          currentItem.epgId = val;
        } else if (key === 'tvg-name' && !currentItem.name) {
          currentItem.name = val;
        } else if (key === 'tvg-logo' || key === 'logo' || key === 'tvg-icon') {
          currentItem.logo = val;
        } else if (key === 'group-title' || key === 'group') {
          if (val) currentItem.category = val;
        } else if (key === 'tvg-chno') {
          const chno = parseInt(val, 10);
          if (!isNaN(chno)) currentItem.number = chno;
        }
      }
    } else if (line.startsWith('#EXTGRP:') && currentItem) {
      const grp = line.substring(8).trim();
      if (grp) currentItem.category = grp;
    } else if (line.charCodeAt(0) !== 35 && currentItem) {
      currentItem.streamUrl = line;
      const urlLower = line.toLowerCase();
      const catLower = (currentItem.category || '').toLowerCase();
      const nameLower = (currentItem.name || '').toLowerCase();

      const isSeries = 
        urlLower.includes('/series/') || 
        catLower.includes('serie') || 
        catLower.includes('series') ||
        /\b[st]\d{1,2}[ex]\d{1,2}\b/i.test(nameLower);

      const isMovie = 
        !isSeries && (
          urlLower.includes('/movie/') || 
          catLower.includes('pelicula') || 
          catLower.includes('pel·lícula') || 
          catLower.includes('pel·licula') || 
          catLower.includes('pelis') || 
          catLower.includes('movies') || 
          catLower.includes('cinema') || 
          catLower.includes('vod') ||
          urlLower.endsWith('.mp4') ||
          urlLower.endsWith('.mkv') ||
          urlLower.endsWith('.avi')
        );

      if (isSeries) {
        const seasonMatch = nameLower.match(/[st](\d{1,2})[ex](\d{1,2})/i);
        const seasonNum = seasonMatch ? parseInt(seasonMatch[1], 10) : 1;
        const episodeNum = seasonMatch ? parseInt(seasonMatch[2], 10) : 1;
        const cleanSeriesTitle = currentItem.name.replace(/[st]\d{1,2}[ex]\d{1,2}.*$/i, '').trim() || currentItem.name;

        seriesList.push({
          id: currentItem.id,
          sourceId,
          name: currentItem.name,
          seriesTitle: cleanSeriesTitle,
          category: currentItem.category || 'Sèries',
          poster: currentItem.logo,
          season: seasonNum,
          episode: episodeNum,
          streamUrl: currentItem.streamUrl,
          userAgent: defaultUa
        });

        const catKey = `series_${currentItem.category || 'Sèries'}`;
        const existing = categoryMap.get(catKey);
        if (existing) {
          existing.count++;
        } else {
          categoryMap.set(catKey, {
            id: catKey,
            name: currentItem.category || 'Sèries',
            type: 'series',
            count: 1
          });
        }
      } else if (isMovie) {
        vodMovies.push({
          id: currentItem.id,
          sourceId,
          name: currentItem.name,
          category: currentItem.category || 'Pel·lícules',
          poster: currentItem.logo,
          streamUrl: currentItem.streamUrl,
          userAgent: defaultUa
        });

        const catKey = `movie_${currentItem.category || 'Pel·lícules'}`;
        const existing = categoryMap.get(catKey);
        if (existing) {
          existing.count++;
        } else {
          categoryMap.set(catKey, {
            id: catKey,
            name: currentItem.category || 'Pel·lícules',
            type: 'movie',
            count: 1
          });
        }
      } else {
        liveChannels.push(currentItem);

        const catKey = `live_${currentItem.category || 'General'}`;
        const existing = categoryMap.get(catKey);
        if (existing) {
          existing.count++;
        } else {
          categoryMap.set(catKey, {
            id: catKey,
            name: currentItem.category || 'General',
            type: 'live',
            count: 1
          });
        }
      }

      currentItem = null;
    }
  }

  return {
    liveChannels,
    vodMovies,
    seriesList,
    categories: Array.from(categoryMap.values())
  };
}

/**
 * Test IPTV connection for M3U or Xtream Codes
 */
export async function testIptvConnection(
  source: Partial<IptvSource>
): Promise<{ success: boolean; latency?: number; message: string; userInfo?: any; serverInfo?: any }> {
  const ua = source.userAgent || DEFAULT_USER_AGENT;

  // On Native Android, attempt Direct Xtream or Direct M3U check first (zero CORS, zero proxy dependency)
  if (isNativeAndroidApp()) {
    if (source.type === 'xtream' && source.serverUrl && source.username && source.password) {
      try {
        const directEndpoint = `${source.serverUrl.replace(/\/+$/, '')}/player_api.php?username=${encodeURIComponent(source.username)}&password=${encodeURIComponent(source.password)}`;
        let responseJson: any = null;
        if (typeof window !== 'undefined' && (window as any).Android && typeof (window as any).Android.fetchUrl === 'function') {
          const raw = (window as any).Android.fetchUrl(directEndpoint);
          if (raw) responseJson = JSON.parse(raw);
        }
        if (!responseJson) {
          const directRes = await fetchWithTimeout(directEndpoint, {}, 10000);
          if (directRes.ok) responseJson = await directRes.json();
        }
        if (responseJson?.user_info?.auth === 1 || responseJson?.user_info?.status === 'Active') {
          return {
            success: true,
            message: 'Connexió directa amb Xtream Codes verificada amb èxit!',
            userInfo: responseJson.user_info,
            serverInfo: responseJson.server_info
          };
        }
      } catch (directErr) {
        console.warn("Direct Android Xtream test failed, falling back to backend:", directErr);
      }
    }
  }

  const targetUrl = buildApiUrl('/api/iptv/test-connection');

  try {
    const res = await fetchWithTimeout(targetUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: source.type || 'm3u',
        url: source.url,
        serverUrl: source.serverUrl,
        username: source.username,
        password: source.password,
        userAgent: ua
      })
    }, 15000);
    const text = await res.text();
    let data: any;
    try {
      data = JSON.parse(text);
    } catch {
      return {
        success: false,
        message: `El servidor ha retornat una resposta no vàlida (HTML/Text). Comprova la URL de la connexió.`
      };
    }
    return data;
  } catch (err: any) {
    // Direct client fallback for Xtream if backend is unreachable
    if (source.type === 'xtream' && source.serverUrl && source.username && source.password) {
      try {
        const directEndpoint = `${source.serverUrl.replace(/\/+$/, '')}/player_api.php?username=${encodeURIComponent(source.username)}&password=${encodeURIComponent(source.password)}`;
        const directRes = await fetchWithTimeout(directEndpoint, {}, 10000);
        if (directRes.ok) {
          const directData = await directRes.json();
          if (directData?.user_info?.auth === 1 || directData?.user_info?.status === 'Active') {
            return {
              success: true,
              message: 'Connexió directa amb Xtream Codes verificada amb èxit!',
              userInfo: directData.user_info,
              serverInfo: directData.server_info
            };
          }
        }
      } catch {}
    }

    return {
      success: false,
      message: err.message || 'Error en connectar amb el servidor IPTV.'
    };
  }
}

/**
 * Sync and fetch an IPTV Source (M3U or Xtream Codes)
 */
export async function syncIptvSource(
  source: IptvSource,
  onProgress?: (progress: IptvSyncProgress) => void
): Promise<{
  channels: IptvChannel[];
  movies: IptvVodMovie[];
  series: IptvSeries[];
  categories: IptvCategory[];
}> {
  onProgress?.({
    sourceId: source.id,
    sourceName: source.name,
    status: 'connecting',
    progressPercent: 10,
    message: 'Connectant amb la font IPTV...',
    liveCount: 0,
    moviesCount: 0,
    seriesCount: 0
  });

  if (source.type === 'xtream') {
    return await syncXtreamSource(source, onProgress);
  } else {
    return await syncM3uSource(source, onProgress);
  }
}

/**
 * Synchronize M3U playlist source
 */
async function syncM3uSource(
  source: IptvSource,
  onProgress?: (progress: IptvSyncProgress) => void
): Promise<{
  channels: IptvChannel[];
  movies: IptvVodMovie[];
  series: IptvSeries[];
  categories: IptvCategory[];
}> {
  const ua = source.userAgent || DEFAULT_USER_AGENT;

  onProgress?.({
    sourceId: source.id,
    sourceName: source.name,
    status: 'downloading',
    progressPercent: 30,
    message: 'Descarregant i analitzant la llista M3U...',
    liveCount: 0,
    moviesCount: 0,
    seriesCount: 0
  });

  let data: any = null;

  // 1. Primary Strategy: Server-side M3U Fetch Proxy
  try {
    const fetchApiUrl = buildApiUrl('/api/iptv/m3u/fetch');
    const res = await fetchWithTimeout(fetchApiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url: source.url,
        m3uContent: source.m3uContent,
        userAgent: ua,
        sourceId: source.id
      })
    }, 28000);

    const responseText = await res.text();
    try {
      data = JSON.parse(responseText);
    } catch {
      if (responseText.toLowerCase().includes('<!doctype') || responseText.toLowerCase().includes('<html')) {
        throw new Error(`El servidor ha retornat una pàgina HTML en lloc d'un fitxer M3U o JSON (possible bloqueig, enllaç no directe o error HTTP ${res.status}). Comprova la URL de la llista.`);
      }
      throw new Error(`Resposta no vàlida del servidor (${res.status}): ${responseText.slice(0, 100)}`);
    }

    if (!res.ok || data.error) {
      throw new Error(data.error || `Error HTTP ${res.status} en descarregar la llista M3U.`);
    }
  } catch (backendErr: any) {
    console.warn("Backend M3U fetch failed, attempting client-side fallbacks:", backendErr);

    // 2. Secondary Strategy: Native Android Bridge or Direct Client Fetch Fallback
    let rawM3uText = source.m3uContent || '';

    if (!rawM3uText && source.url) {
      // Android bridge
      if (typeof window !== 'undefined' && (window as any).Android && typeof (window as any).Android.fetchUrl === 'function') {
        try {
          rawM3uText = (window as any).Android.fetchUrl(source.url);
        } catch (androidErr) {
          console.warn("Android native fetchUrl failed:", androidErr);
        }
      }

      // Direct browser fetch
      if (!rawM3uText) {
        try {
          const directRes = await fetchWithTimeout(source.url, { headers: { 'Accept': '*/*' } }, 15000);
          if (directRes.ok) {
            rawM3uText = await directRes.text();
          }
        } catch {}
      }

      // Public CORS Proxy fallback
      if (!rawM3uText) {
        try {
          const proxyRes = await fetchWithTimeout(`https://api.allorigins.win/raw?url=${encodeURIComponent(source.url)}`, {}, 15000);
          if (proxyRes.ok) {
            rawM3uText = await proxyRes.text();
          }
        } catch {}
      }
    }

    if (rawM3uText && (rawM3uText.includes('#EXTM3U') || rawM3uText.includes('#EXTINF'))) {
      const parsed = parseClientM3uText(rawM3uText, source.id, ua);
      data = {
        ok: true,
        success: true,
        sourceId: source.id,
        userAgent: ua,
        totalItems: parsed.liveChannels.length + parsed.vodMovies.length + parsed.seriesList.length,
        liveCount: parsed.liveChannels.length,
        moviesCount: parsed.vodMovies.length,
        seriesCount: parsed.seriesList.length,
        categories: parsed.categories,
        channels: parsed.liveChannels,
        movies: parsed.vodMovies,
        series: parsed.seriesList
      };
    } else {
      throw new Error(backendErr.message || 'No s\'ha pogut descarregar la llista M3U (Error de xarxa o "failed to fetch"). Comprova la URL de la llista i la connexió a internet.');
    }
  }

  onProgress?.({
    sourceId: source.id,
    sourceName: source.name,
    status: 'saving',
    progressPercent: 80,
    message: `Emmagatzemant ${data.liveCount} canals, ${data.moviesCount} pel·lícules i ${data.seriesCount} sèries...`,
    liveCount: data.liveCount,
    moviesCount: data.moviesCount,
    seriesCount: data.seriesCount
  });

  const channels: IptvChannel[] = (data.channels || []).map((ch: any) => ({
    ...ch,
    sourceId: source.id,
    sourceName: source.name,
    userAgent: ua,
    directStreamUrl: ch.streamUrl,
    streamUrl: getProxiedStreamUrl(ch.streamUrl, ua)
  }));

  const movies: IptvVodMovie[] = (data.movies || []).map((m: any) => ({
    ...m,
    sourceId: source.id,
    sourceName: source.name,
    userAgent: ua,
    directStreamUrl: m.streamUrl,
    streamUrl: getProxiedStreamUrl(m.streamUrl, ua)
  }));

  // Group series episodes by series title
  const seriesMap = new Map<string, IptvSeries>();
  (data.series || []).forEach((sItem: any) => {
    const seriesTitle = sItem.seriesTitle || sItem.name;
    const seriesId = `${source.id}_series_${encodeURIComponent(seriesTitle.toLowerCase().trim())}`;
    
    let existing = seriesMap.get(seriesId);
    if (!existing) {
      existing = {
        id: seriesId,
        sourceId: source.id,
        sourceName: source.name,
        name: seriesTitle,
        category: sItem.category || 'Sèries',
        poster: sItem.poster,
        seasons: []
      };
      seriesMap.set(seriesId, existing);
    }

    const sNum = sItem.season || 1;
    const epNum = sItem.episode || 1;
    
    let season = existing.seasons?.find(s => s.seasonNumber === sNum);
    if (!season) {
      season = { seasonNumber: sNum, episodes: [] };
      existing.seasons = existing.seasons || [];
      existing.seasons.push(season);
    }

    season.episodes.push({
      id: `${seriesId}_s${sNum}_e${epNum}`,
      seriesId,
      season: sNum,
      episode: epNum,
      title: sItem.name,
      directStreamUrl: sItem.streamUrl,
      streamUrl: getProxiedStreamUrl(sItem.streamUrl, ua),
      poster: sItem.poster
    });
  });

  const series: IptvSeries[] = Array.from(seriesMap.values()).map(s => ({
    ...s,
    seasonsCount: s.seasons?.length || 0,
    episodesCount: s.seasons?.reduce((acc, sea) => acc + sea.episodes.length, 0) || 0
  }));

  const categories: IptvCategory[] = (data.categories || []).map((cat: any) => ({
    ...cat,
    sourceId: source.id,
    sourceName: source.name
  }));

  // Save to IndexedDB
  await cacheIptvData(source.id, channels, movies, series, categories);

  onProgress?.({
    sourceId: source.id,
    sourceName: source.name,
    status: 'completed',
    progressPercent: 100,
    message: 'Sincronització completada amb èxit!',
    liveCount: channels.length,
    moviesCount: movies.length,
    seriesCount: series.length
  });

  return { channels, movies, series, categories };
}

/**
 * Fetch Xtream API action with backend proxy and direct fallback
 */
async function fetchXtreamAction(
  server: string,
  username: string,
  password: string,
  action: string,
  ua: string,
  extraParams = '',
  timeoutMs = 18000
): Promise<any[]> {
  const isAndroid = isNativeAndroidApp();

  // If in native Android, prioritize DIRECT fetch and native Android bridge (no CORS, no proxy bottleneck)
  if (isAndroid) {
    // Android bridge with native networking
    if (typeof window !== 'undefined' && (window as any).Android && typeof (window as any).Android.fetchUrl === 'function') {
      try {
        const directUrl = `${server}/player_api.php?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&action=${encodeURIComponent(action)}${extraParams}`;
        const text = (window as any).Android.fetchUrl(directUrl);
        if (text) {
          const data = JSON.parse(text);
          if (Array.isArray(data)) return data;
          if (data && typeof data === 'object') return data;
        }
      } catch (androidBridgeErr) {
        console.warn(`Android bridge fetch failed for action ${action}:`, androidBridgeErr);
      }
    }

    // Direct fetch (Android WebView has no CORS on direct endpoints)
    try {
      const directUrl = `${server}/player_api.php?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&action=${encodeURIComponent(action)}${extraParams}`;
      const res = await fetchWithTimeout(directUrl, {}, timeoutMs);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) return data;
        if (data && typeof data === 'object') return data;
      }
    } catch (directErr) {
      console.warn(`Direct Android fetch failed for action ${action}, will attempt backend proxy:`, directErr);
    }
  }

  // Web Browser / Cloud Run proxy strategy
  try {
    const proxyUrl = buildApiUrl(
      `/api/iptv/xtream/action?serverUrl=${encodeURIComponent(server)}&username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&action=${encodeURIComponent(action)}&userAgent=${encodeURIComponent(ua)}${extraParams}`
    );
    const res = await fetchWithTimeout(proxyUrl, {}, timeoutMs);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data)) return data;
      if (data && typeof data === 'object') return data;
    }
  } catch (proxyErr) {
    console.warn(`Backend Xtream proxy failed for action ${action}, trying direct fetch:`, proxyErr);
  }

  // Fallback for browsers if backend is down but server has CORS headers
  if (!isAndroid) {
    try {
      const directUrl = `${server}/player_api.php?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&action=${encodeURIComponent(action)}${extraParams}`;
      const res = await fetchWithTimeout(directUrl, {}, timeoutMs);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) return data;
        if (data && typeof data === 'object') return data;
      }
    } catch {}
  }

  return [];
}

/**
 * Synchronize Xtream Codes API source
 */
async function syncXtreamSource(
  source: IptvSource,
  onProgress?: (progress: IptvSyncProgress) => void
): Promise<{
  channels: IptvChannel[];
  movies: IptvVodMovie[];
  series: IptvSeries[];
  categories: IptvCategory[];
}> {
  const ua = source.userAgent || DEFAULT_USER_AGENT;
  const server = source.serverUrl?.replace(/\/+$/, '') || '';
  const username = source.username || '';
  const password = source.password || '';

  if (!server || !username || !password) {
    throw new Error('Cal especificar la URL del servidor, l\'usuari i la contrasenya d\'Xtream Codes.');
  }

  // 1. Initial Account Status Validation & Fetch categories
  onProgress?.({
    sourceId: source.id,
    sourceName: source.name,
    status: 'downloading',
    progressPercent: 10,
    message: 'Verificant estat del compte i categories...',
    liveCount: 0,
    moviesCount: 0,
    seriesCount: 0
  });

  // Verify account auth & expiration first (fail fast with descriptive error)
  try {
    const authCheckUrl = buildApiUrl(
      `/api/iptv/xtream/action?serverUrl=${encodeURIComponent(server)}&username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&action=&userAgent=${encodeURIComponent(ua)}`
    );
    const authRes = await fetchWithTimeout(authCheckUrl, {}, 10000);
    if (authRes.ok) {
      const authData = await authRes.json();
      const userInfo = authData?.user_info;
      if (userInfo) {
        if (userInfo.auth === 0) {
          throw new Error('Usuari o contrasenya incorrectes al servidor Xtream Codes.');
        }
        if (userInfo.status === 'Banned' || userInfo.status === 'Disabled') {
          throw new Error(`El compte està en estat bloquejat (${userInfo.status}) pel proveïdor.`);
        }
        if (userInfo.status === 'Expired') {
          throw new Error('El compte d\'aquest servidor Xtream Codes ha caducat.');
        }
      }
    }
  } catch (authErr: any) {
    if (authErr.message?.includes('Usuari o contrasenya') || authErr.message?.includes('caducat') || authErr.message?.includes('bloquejat')) {
      throw authErr;
    }
    // Network or other transient issues: continue with stream download
  }

  const [liveCatsRes, vodCatsRes, seriesCatsRes] = await Promise.all([
    fetchXtreamAction(server, username, password, 'get_live_categories', ua, '', 12000),
    fetchXtreamAction(server, username, password, 'get_vod_categories', ua, '', 12000),
    fetchXtreamAction(server, username, password, 'get_series_categories', ua, '', 12000)
  ]);

  const liveCats = Array.isArray(liveCatsRes) ? liveCatsRes : [];
  const vodCats = Array.isArray(vodCatsRes) ? vodCatsRes : [];
  const seriesCats = Array.isArray(seriesCatsRes) ? seriesCatsRes : [];

  const catMap = new Map<string, string>();
  const categories: IptvCategory[] = [];

  liveCats.forEach((c: any) => {
    catMap.set(`live_${c.category_id}`, c.category_name);
    categories.push({
      id: `live_${c.category_id}`,
      name: c.category_name,
      type: 'live',
      sourceId: source.id,
      sourceName: source.name
    });
  });

  vodCats.forEach((c: any) => {
    catMap.set(`vod_${c.category_id}`, c.category_name);
    categories.push({
      id: `movie_${c.category_id}`,
      name: c.category_name,
      type: 'movie',
      sourceId: source.id,
      sourceName: source.name
    });
  });

  seriesCats.forEach((c: any) => {
    catMap.set(`series_${c.category_id}`, c.category_name);
    categories.push({
      id: `series_${c.category_id}`,
      name: c.category_name,
      type: 'series',
      sourceId: source.id,
      sourceName: source.name
    });
  });

  // 2. Fetch Live TV Streams
  onProgress?.({
    sourceId: source.id,
    sourceName: source.name,
    status: 'downloading',
    progressPercent: 35,
    message: 'Obtenint canals de TV en directe...',
    liveCount: 0,
    moviesCount: 0,
    seriesCount: 0
  });

  const liveStreamsRes = await fetchXtreamAction(server, username, password, 'get_live_streams', ua, '', 25000);
  const rawLive = Array.isArray(liveStreamsRes) ? liveStreamsRes : [];

  // 3. Fetch VOD Streams
  onProgress?.({
    sourceId: source.id,
    sourceName: source.name,
    status: 'downloading',
    progressPercent: 60,
    message: `Obtinguts ${rawLive.length} canals. Descarregant pel·lícules (VOD)...`,
    liveCount: rawLive.length,
    moviesCount: 0,
    seriesCount: 0
  });

  const vodStreamsRes = await fetchXtreamAction(server, username, password, 'get_vod_streams', ua, '', 25000);
  const rawVod = Array.isArray(vodStreamsRes) ? vodStreamsRes : [];

  // 4. Fetch Series
  onProgress?.({
    sourceId: source.id,
    sourceName: source.name,
    status: 'downloading',
    progressPercent: 80,
    message: `Obtinguts ${rawVod.length} pel·lícules. Descarregant sèries...`,
    liveCount: rawLive.length,
    moviesCount: rawVod.length,
    seriesCount: 0
  });

  const seriesListRes = await fetchXtreamAction(server, username, password, 'get_series', ua, '', 25000);
  const rawSeries = Array.isArray(seriesListRes) ? seriesListRes : [];

  // Validation: Check if no data was returned at all
  if (rawLive.length === 0 && rawVod.length === 0 && rawSeries.length === 0) {
    throw new Error(`No s'ha pogut obtenir cap canal o contingut del servidor Xtream Codes (${server}). Comprova que l'adreça del servidor, l'usuari i la contrasenya siguin correctes, i que el compte estigui actiu.`);
  }

  const channels: IptvChannel[] = rawLive.map((item: any) => {
    const streamExtension = item.container_extension || 'm3u8';
    const directUrl = `${server}/live/${username}/${password}/${item.stream_id}.${streamExtension}`;
    const catName = catMap.get(`live_${item.category_id}`) || 'General';

    return {
      id: `${source.id}_live_${item.stream_id}`,
      sourceId: source.id,
      sourceName: source.name,
      name: item.name || `Canal ${item.stream_id}`,
      streamUrl: getProxiedStreamUrl(directUrl, ua),
      directStreamUrl: directUrl,
      logo: item.stream_icon,
      category: catName,
      categoryId: item.category_id,
      epgId: item.epg_channel_id,
      number: item.num ? parseInt(item.num, 10) : undefined,
      userAgent: ua
    };
  });

  const movies: IptvVodMovie[] = rawVod.map((item: any) => {
    const ext = item.container_extension || 'mp4';
    const directUrl = `${server}/movie/${username}/${password}/${item.stream_id}.${ext}`;
    const catName = catMap.get(`vod_${item.category_id}`) || 'Pel·lícules';

    return {
      id: `${source.id}_movie_${item.stream_id}`,
      sourceId: source.id,
      sourceName: source.name,
      name: item.name || `Pel·lícula ${item.stream_id}`,
      streamUrl: getProxiedStreamUrl(directUrl, ua),
      directStreamUrl: directUrl,
      poster: item.stream_icon,
      category: catName,
      categoryId: item.category_id,
      rating: item.rating ? parseFloat(item.rating) : undefined,
      year: item.year ? String(item.year) : undefined,
      plot: item.plot,
      streamId: item.stream_id,
      containerExtension: ext
    };
  });

  const series: IptvSeries[] = rawSeries.map((item: any) => {
    const catName = catMap.get(`series_${item.category_id}`) || 'Sèries';

    return {
      id: `${source.id}_series_${item.series_id}`,
      sourceId: source.id,
      sourceName: source.name,
      name: item.name || `Sèrie ${item.series_id}`,
      poster: item.cover,
      backdrop: item.backdrop_path ? (Array.isArray(item.backdrop_path) ? item.backdrop_path[0] : item.backdrop_path) : undefined,
      category: catName,
      categoryId: item.category_id,
      rating: item.rating ? parseFloat(item.rating) : undefined,
      year: item.releaseDate ? String(item.releaseDate).slice(0, 4) : undefined,
      plot: item.plot,
      genre: item.genre,
      seriesId: item.series_id,
      seasons: []
    };
  });

  // Save to IndexedDB
  onProgress?.({
    sourceId: source.id,
    sourceName: source.name,
    status: 'saving',
    progressPercent: 92,
    message: `Emmagatzemant ${channels.length} canals, ${movies.length} pel·lícules i ${series.length} sèries...`,
    liveCount: channels.length,
    moviesCount: movies.length,
    seriesCount: series.length
  });

  await cacheIptvData(source.id, channels, movies, series, categories);

  onProgress?.({
    sourceId: source.id,
    sourceName: source.name,
    status: 'completed',
    progressPercent: 100,
    message: 'Sincronització completada amb èxit!',
    liveCount: channels.length,
    moviesCount: movies.length,
    seriesCount: series.length
  });

  return { channels, movies, series, categories };
}

/**
 * Fetch detailed series seasons and episodes from Xtream Codes on demand
 */
export async function fetchXtreamSeriesInfo(
  source: IptvSource,
  seriesId: string | number
): Promise<IptvSeries | null> {
  const ua = source.userAgent || DEFAULT_USER_AGENT;
  const server = source.serverUrl?.replace(/\/+$/, '') || '';
  const username = source.username || '';
  const password = source.password || '';

  try {
    let data: any = null;

    // 1. Try Backend Proxy
    try {
      const proxyUrl = buildApiUrl(
        `/api/iptv/xtream/action?serverUrl=${encodeURIComponent(server)}&username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&action=get_series_info&series_id=${encodeURIComponent(String(seriesId))}&userAgent=${encodeURIComponent(ua)}`
      );
      const res = await fetch(proxyUrl);
      if (res.ok) {
        data = await res.json();
      }
    } catch {}

    // 2. Fallback to Direct Xtream Call
    if (!data) {
      try {
        const directUrl = `${server}/player_api.php?username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&action=get_series_info&series_id=${encodeURIComponent(String(seriesId))}`;
        const res = await fetch(directUrl);
        if (res.ok) {
          data = await res.json();
        }
      } catch {}
    }

    if (!data) return null;

    const info = data.info || {};
    const episodesBySeason = data.episodes || {};

    const seasons: { seasonNumber: number; name?: string; episodes: any[] }[] = [];

    Object.keys(episodesBySeason).forEach(seasonKey => {
      const sNum = parseInt(seasonKey, 10);
      const epsList = Array.isArray(episodesBySeason[seasonKey]) ? episodesBySeason[seasonKey] : [];

      const episodes = epsList.map((ep: any) => {
        const epId = ep.id || ep.stream_id || ep.series_id;
        const ext = ep.container_extension || 'mp4';
        const directUrl = `${server}/series/${username}/${password}/${epId}.${ext}`;
        return {
          id: `${source.id}_series_${seriesId}_s${sNum}_e${ep.episode_num || epId}`,
          seriesId: String(seriesId),
          season: sNum,
          episode: ep.episode_num ? parseInt(ep.episode_num, 10) : 1,
          title: ep.title || ep.name || `Episodi ${ep.episode_num || 1}`,
          directStreamUrl: directUrl,
          streamUrl: getProxiedStreamUrl(directUrl, ua),
          plot: ep.info?.plot || ep.plot,
          duration: ep.info?.duration_secs || ep.duration,
          poster: ep.info?.movie_image || info.cover,
          containerExtension: ext
        };
      });

      seasons.push({
        seasonNumber: sNum,
        name: `Temporada ${sNum}`,
        episodes
      });
    });

    seasons.sort((a, b) => a.seasonNumber - b.seasonNumber);

    return {
      id: `${source.id}_series_${seriesId}`,
      sourceId: source.id,
      sourceName: source.name,
      name: info.name || `Sèrie ${seriesId}`,
      poster: info.cover,
      backdrop: info.backdrop_path ? (Array.isArray(info.backdrop_path) ? info.backdrop_path[0] : info.backdrop_path) : undefined,
      category: info.genre || 'Sèries',
      rating: info.rating ? parseFloat(info.rating) : undefined,
      plot: info.plot,
      genre: info.genre,
      year: info.releaseDate ? String(info.releaseDate).slice(0, 4) : undefined,
      seriesId,
      seasonsCount: seasons.length,
      episodesCount: seasons.reduce((acc, s) => acc + s.episodes.length, 0),
      seasons
    };
  } catch (err) {
    console.error('fetchXtreamSeriesInfo error:', err);
    return null;
  }
}

async function safeFetchJson(url: string, defaultVal: any = []): Promise<any> {
  try {
    const res = await fetch(url);
    const text = await res.text();
    return JSON.parse(text);
  } catch {
    return defaultVal;
  }
}

/**
 * Cache items in IndexedDB
 */
async function cacheIptvData(
  sourceId: string,
  channels: IptvChannel[],
  movies: IptvVodMovie[],
  series: IptvSeries[],
  categories: IptvCategory[]
) {
  try {
    const cachePromise = (async () => {
      const db = await openIptvDb();
      const tx = db.transaction(['channels', 'movies', 'series', 'categories'], 'readwrite');

      const chStore = tx.objectStore('channels');
      const mvStore = tx.objectStore('movies');
      const srStore = tx.objectStore('series');
      const catStore = tx.objectStore('categories');

      const newChannelIds = new Set(channels.map(c => c.id));
      const newMovieIds = new Set(movies.map(m => m.id));
      const newSeriesIds = new Set(series.map(s => s.id));
      const newCategoryIds = new Set(categories.map(c => c.id));

      // Purge old/deleted items for this source
      const purgeOldRecords = (store: IDBObjectStore, newIds: Set<string>) => {
        const req = store.openCursor();
        req.onsuccess = (e) => {
          const cursor = (e.target as IDBRequest).result as IDBCursorWithValue;
          if (cursor) {
            const val = cursor.value;
            if (val && val.sourceId === sourceId && !newIds.has(val.id)) {
              cursor.delete();
            }
            cursor.continue();
          }
        };
      };

      purgeOldRecords(chStore, newChannelIds);
      purgeOldRecords(mvStore, newMovieIds);
      purgeOldRecords(srStore, newSeriesIds);
      purgeOldRecords(catStore, newCategoryIds);

      for (let i = 0; i < channels.length; i++) {
        chStore.put(channels[i]);
      }

      for (let i = 0; i < movies.length; i++) {
        mvStore.put(movies[i]);
      }

      for (let i = 0; i < series.length; i++) {
        srStore.put(series[i]);
      }

      for (let i = 0; i < categories.length; i++) {
        catStore.put(categories[i]);
      }

      return new Promise<boolean>((resolve) => {
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
        tx.onabort = () => resolve(false);
      });
    })();

    const timeoutPromise = new Promise<boolean>((resolve) => {
      setTimeout(() => resolve(false), 8000);
    });

    await Promise.race([cachePromise, timeoutPromise]);
  } catch (err) {
    console.warn('IndexedDB caching warning:', err);
  }
}

/**
 * Load all cached IPTV items from IndexedDB
 */
export async function loadCachedIptvData(): Promise<{
  channels: IptvChannel[];
  movies: IptvVodMovie[];
  series: IptvSeries[];
  categories: IptvCategory[];
}> {
  try {
    const loadPromise = (async () => {
      const db = await openIptvDb();
      const tx = db.transaction(['channels', 'movies', 'series', 'categories'], 'readonly');

      const getAll = (storeName: string): Promise<any[]> => {
        return new Promise((resolve) => {
          try {
            const store = tx.objectStore(storeName);
            const req = store.getAll();
            req.onsuccess = () => resolve(req.result || []);
            req.onerror = () => resolve([]);
          } catch {
            resolve([]);
          }
        });
      };

      const [channels, movies, series, categories] = await Promise.all([
        getAll('channels'),
        getAll('movies'),
        getAll('series'),
        getAll('categories')
      ]);

      return { channels, movies, series, categories };
    })();

    const timeoutPromise = new Promise<{
      channels: IptvChannel[];
      movies: IptvVodMovie[];
      series: IptvSeries[];
      categories: IptvCategory[];
    }>((resolve) => {
      setTimeout(() => resolve({ channels: [], movies: [], series: [], categories: [] }), 4000);
    });

    return await Promise.race([loadPromise, timeoutPromise]);
  } catch {
    return { channels: [], movies: [], series: [], categories: [] };
  }
}

/**
 * Clear cached IPTV items for a deleted source
 */
export async function clearCachedSource(sourceId: string) {
  try {
    const db = await openIptvDb();
    const stores = ['channels', 'movies', 'series', 'categories'];
    for (const sName of stores) {
      const tx = db.transaction(sName, 'readwrite');
      const store = tx.objectStore(sName);
      const req = store.openCursor();
      req.onsuccess = (e: any) => {
        const cursor = e.target.result;
        if (cursor) {
          if (cursor.value?.sourceId === sourceId) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
    }
  } catch {}
}

